import React, { useMemo } from "react";
import { Alert, Container, LinearProgress } from "@mui/material";

import {
    buildCustomerOptions,
    findMatchingCustomerOption,
    resolveCustomerOption,
} from "../shared/customerSelectUtils";
import RecurringInvoiceDialog from "./recurring-invoice-management/RecurringInvoiceDialog";
import RecurringInvoiceHeader from "./recurring-invoice-management/RecurringInvoiceHeader";
import RecurringInvoiceList from "./recurring-invoice-management/RecurringInvoiceList";
import RecurringInvoiceToast from "./recurring-invoice-management/RecurringInvoiceToast";
import useRecurringInvoiceManagement from "./recurring-invoice-management/useRecurringInvoiceManagement";

export default function RecurringInvoiceManagement() {
    const {
        entries,
        loading,
        error,
        customers,
        profil,
        dialogOpen,
        dialogMode,
        form,
        saving,
        fieldErrors,
        articleErrors,
        expandedLogs,
        toast,
        customerLookup,
        sortedArticles,
        setToast,
        loadData,
        openCreateDialog,
        openEditDialog,
        closeDialog,
        handleFormChange,
        handleTemplateChange,
        handleTemplateDateChange,
        handleRemoveArticle,
        handleAddArticle,
        handleSave,
        handleDelete,
        handleToggleLogs,
    } = useRecurringInvoiceManagement();

    const customerOptions = useMemo(() => buildCustomerOptions(customers), [customers]);
    const customersLoading = loading && customers.length === 0;
    const selectedCustomerOption = useMemo(
        () => resolveCustomerOption(customerOptions, form.customerId, ""),
        [customerOptions, form.customerId]
    );

    const handleCustomerChange = (_, value) => {
        if (typeof value === "string") {
            const matched = findMatchingCustomerOption(customerOptions, value);
            handleFormChange({ customerId: matched?.id || "" });
            return;
        }
        handleFormChange({ customerId: value?.id || "" });
    };

    const handleCustomerInputChange = (_, inputValue, reason) => {
        if (reason !== "input") return;
        const matched = findMatchingCustomerOption(customerOptions, inputValue);
        handleFormChange({ customerId: matched?.id || "" });
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <RecurringInvoiceHeader onRefresh={loadData} onCreate={openCreateDialog} />

            {loading && <LinearProgress sx={{ mb: 3 }} />}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <RecurringInvoiceList
                entries={entries}
                expandedLogs={expandedLogs}
                customerLookup={customerLookup}
                onToggleLogs={handleToggleLogs}
                onEdit={openEditDialog}
                onDelete={handleDelete}
            />

            <RecurringInvoiceDialog
                open={dialogOpen}
                mode={dialogMode}
                form={form}
                saving={saving}
                fieldErrors={fieldErrors}
                articleErrors={articleErrors}
                customerOptions={customerOptions}
                customersLoading={customersLoading}
                selectedCustomerOption={selectedCustomerOption}
                onClose={closeDialog}
                onFormChange={handleFormChange}
                onCustomerChange={handleCustomerChange}
                onCustomerInputChange={handleCustomerInputChange}
                onTemplateDateChange={handleTemplateDateChange}
                onAddArticle={handleAddArticle}
                onRemoveArticle={handleRemoveArticle}
                onTemplateChange={handleTemplateChange}
                onSave={handleSave}
                sortedArticles={sortedArticles}
                profil={profil}
            />

            <RecurringInvoiceToast
                toast={toast}
                onClose={() => setToast((prev) => ({ ...prev, open: false }))}
            />
        </Container>
    );
}
